import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import usePageMeta, { SITE_URL } from '../hooks/usePageMeta';
import { PLANS, formatDollars } from '../config/plans';
import useLivePlans from '../hooks/useLivePlans';
import CommercialWaitlist from '../components/CommercialWaitlist';

const DESCRIPTION = 'Snow removal software for contractors: route management, Storm Mode, customer texting (Pro), per-storm pricing by snow depth and driveway size, and customer intake pages.';

// Structured data for search engines and AI assistants. The offers are built
// from the same plan list the plans strip renders, so the two cannot drift:
// config/plans.js in the pre-rendered HTML, the live prices from
// GET /api/plans once the page has loaded (hooks/useLivePlans.js).
export const structuredData = (plans = PLANS) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'SnowTrack',
      url: SITE_URL,
      logo: `${SITE_URL}/icon-512.png`,
    },
    {
      '@type': 'SoftwareApplication',
      name: 'SnowTrack',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: SITE_URL,
      description: DESCRIPTION,
      publisher: { '@id': `${SITE_URL}/#organization` },
      offers: plans.map((plan) => ({
        '@type': 'Offer',
        name: `${plan.name} plan`,
        description: plan.description,
        price: plan.price.toFixed(2),
        priceCurrency: 'USD',
        url: `${SITE_URL}/pricing`,
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: plan.price.toFixed(2),
          priceCurrency: 'USD',
          unitText: 'month',
        },
      })),
    },
  ],
});

function LandingPage() {
  // Wordmark comes from config so the trademark symbol is defined in one
  // place. It used to be hardcoded here without the symbol.
  const { getAppName } = useConfig();
  const plans = useLivePlans();
  const planPrice = (id) => formatDollars(plans.find((plan) => plan.id === id).price);

  usePageMeta({
    title: 'SnowTrack: snow removal software for contractors',
    description: DESCRIPTION,
    path: '/',
    jsonLd: structuredData(plans),
  });

  const features = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Storm Mode',
      description: 'One tap to complete a stop, with photos and notes attached as you go',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      title: 'Route building',
      description: 'Order your stops, optimize with Google Maps, and start from where you actually are',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      ),
      title: 'Customers and properties',
      description: 'One record per customer, as many properties under it as they own, with the full service history on each',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
      title: 'Invoice from your own Square account',
      description: 'Connect Square in Settings and bill a whole route at once. The invoice comes from your business and the money goes to you',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      title: 'Weather that matters',
      description: 'National Weather Service forecasts and snowfall for your service area, on the dashboard',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <path d="M12 18h.01" />
        </svg>
      ),
      title: 'Works offline, installs like an app',
      description: 'A phone, a tablet or a laptop. No app store, no install, and it keeps working when the signal drops',
    },
  ];

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          {/* Snowflake, matching the pricing page header exactly. This was a
              Material "public" globe glyph, which said nothing about snow. */}
          <Link to="/" className="nav-logo">
            <span className="logo-icon">&#10052;&#65039;</span>
            {getAppName()}
          </Link>
          <div className="nav-links">
            <Link to="/pricing" className="nav-link">Pricing</Link>
            <Link to="/login" className="nav-link">Login</Link>
            <Link to="/register" className="nav-btn-primary">Start Free Trial</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Run your snow removal business from the truck
          </h1>
          <p className="hero-subtitle">
            Build the route, work it in Storm Mode, and bill it from your own Square account.
            All of it on your phone.
          </p>
          <div className="hero-buttons">
            <Link to="/register" className="btn-hero-primary">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="btn-hero-secondary">
              View Pricing
            </Link>
          </div>
        </div>
        <div className="hero-snowflakes">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="snowflake" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}>❄</div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="features-title">Everything you need to run your business</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plan limits, stated on the landing page rather than only behind the
          pricing link. The numbers mirror server/config/planLimits.js; the
          prices come from config/plans.js. */}
      <section className="plans-strip-section">
        <div className="plans-strip-container">
          <h2 className="plans-strip-title">Four plans, flat monthly pricing</h2>
          <div className="plans-strip-grid">
            <div className="plans-strip-item">
              <span className="plans-strip-name">Basic ${planPrice('basic')}</span>
              <span className="plans-strip-detail">One person and 25 customers</span>
            </div>
            <div className="plans-strip-item">
              <span className="plans-strip-name">Pro ${planPrice('pro')}</span>
              <span className="plans-strip-detail">3 users and 75 customers</span>
            </div>
            <div className="plans-strip-item">
              <span className="plans-strip-name">Premium ${planPrice('premium')}</span>
              <span className="plans-strip-detail">10 users and 200 customers</span>
            </div>
            <div className="plans-strip-item">
              <span className="plans-strip-name">Enterprise ${planPrice('enterprise')}</span>
              <span className="plans-strip-detail">No limits</span>
            </div>
          </div>
          <p className="plans-strip-note">
            Every plan includes Storm Mode, routes, weather, the intake form and the customer
            portal.
          </p>
          <Link to="/pricing" className="plans-strip-link">
            See what each plan includes
          </Link>
        </div>
      </section>

      {/* Commercial accounts waitlist (2027-28 season). Static text plus a
          form, so the pre-rendered home page carries the heading and copy. */}
      <CommercialWaitlist />

      {/* Coming Soon: LawnTrack */}
      <section className="lawntrack-section">
        <div className="lawntrack-container">
          <div className="lawntrack-badge">Coming Soon</div>
          <div className="lawntrack-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="5" r="3" />
              <path d="M12 8v8" />
              <path d="M8 12l-3 8h14l-3-8" />
              <path d="M6 20c0 0 2-1 6-1s6 1 6 1" />
            </svg>
          </div>
          <h2 className="lawntrack-title">LawnTrack™</h2>
          <p className="lawntrack-subtitle">
            Mowing routes, lawn customers and invoices, on the same account you already have.
          </p>
          <p className="lawntrack-description">
            Built on the same routing, scheduling and invoicing that SnowTrack™ runs on, for
            the months when there is no snow to move.
          </p>
          <span className="btn-lawntrack disabled">
            Coming Spring 2027
          </span>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Start with one free storm</h2>
          <p className="cta-subtitle">
            No credit card. Set up your customers and routes, run a real storm from start to
            finish, then decide.
          </p>
          <div className="cta-buttons">
            <Link to="/register" className="btn-cta-primary">
              Start Free Trial
            </Link>
          </div>
          <Link to="/pricing" className="cta-link">
            View full pricing and features →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <span className="footer-logo">{getAppName()}</span>
          </div>
          <div className="footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          {/* Year comes from the clock, matching the pricing page and the legal
              layout. It was hardcoded to 2025 and went stale on 1 January. */}
          <div className="footer-copyright">
            © {new Date().getFullYear()} SnowTrack™. All rights reserved.<br />
            SnowTrack™ is operated by CW Affiliate Investments, LLC.
          </div>
        </div>
      </footer>

      <style>{`
        .landing-page {
          min-height: 100vh;
          background: #f8fafc;
        }

        /* Navigation */
        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          z-index: 100;
          border-bottom: 1px solid #e2e8f0;
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e88e5;
          text-decoration: none;
        }

        /* Sized as text, not as an SVG box - same rule as the pricing header */
        .logo-icon {
          font-size: 1.5rem;
          line-height: 1;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .nav-link {
          color: #475569;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .nav-link:hover {
          color: #1e88e5;
        }

        .nav-btn-primary {
          background: #1e88e5;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: background 0.2s;
        }

        .nav-btn-primary:hover {
          background: #1565c0;
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 24px 80px;
          overflow: hidden;
        }

        .hero-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #1e88e5 0%, #1565c0 50%, #0d47a1 100%);
          z-index: 0;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
        }

        .hero-title {
          font-size: 3rem;
          font-weight: 800;
          color: white;
          margin: 0 0 24px 0;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .hero-subtitle {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 40px 0;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-hero-primary {
          background: white;
          color: #1e88e5;
          padding: 16px 32px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(0,0,0,0.15);
        }

        .btn-hero-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.2);
        }

        .btn-hero-secondary {
          background: transparent;
          color: white;
          padding: 16px 32px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1.1rem;
          border: 2px solid rgba(255,255,255,0.5);
          transition: background 0.2s, border-color 0.2s;
        }

        .btn-hero-secondary:hover {
          background: rgba(255,255,255,0.1);
          border-color: white;
        }

        /* Snowflakes Animation */
        .hero-snowflakes {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
        }

        .snowflake {
          position: absolute;
          top: -20px;
          color: rgba(255,255,255,0.4);
          font-size: 1rem;
          animation: snowfall linear infinite;
        }

        @keyframes snowfall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0.3;
          }
        }

        /* Features Section */
        .features-section {
          padding: 80px 24px;
          background: white;
        }

        .features-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .features-title {
          text-align: center;
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 48px 0;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }

        .feature-card {
          padding: 32px;
          background: #f8fafc;
          border-radius: 16px;
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.08);
        }

        .feature-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 20px;
          background: #e0f2fe;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1e88e5;
        }

        .feature-icon svg {
          width: 28px;
          height: 28px;
        }

        .feature-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 12px 0;
        }

        .feature-description {
          font-size: 0.95rem;
          color: #64748b;
          margin: 0;
          line-height: 1.5;
        }

        /* Plan limits strip */
        .plans-strip-section {
          padding: 64px 24px;
          background: #ffffff;
          border-top: 1px solid #e2e8f0;
        }

        .plans-strip-container {
          max-width: 1000px;
          margin: 0 auto;
          text-align: center;
        }

        .plans-strip-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 28px 0;
        }

        .plans-strip-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .plans-strip-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 20px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
        }

        .plans-strip-name {
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
        }

        .plans-strip-detail {
          font-size: 0.9rem;
          color: #64748b;
          line-height: 1.45;
        }

        .plans-strip-note {
          margin: 24px auto 16px;
          max-width: 620px;
          font-size: 0.95rem;
          color: #64748b;
          line-height: 1.6;
        }

        .plans-strip-link {
          color: #1e88e5;
          font-weight: 600;
          text-decoration: none;
        }

        .plans-strip-link:hover {
          text-decoration: underline;
        }

        /* Commercial accounts waitlist */
        .commercial-section {
          padding: 72px 24px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-top: 1px solid #e2e8f0;
        }

        .commercial-container {
          max-width: 640px;
          margin: 0 auto;
          text-align: center;
        }

        .commercial-badge {
          display: inline-block;
          background: #1e88e5;
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .commercial-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 12px 0;
          line-height: 1.25;
        }

        .commercial-subtitle {
          font-size: 1.05rem;
          color: #475569;
          margin: 0 0 28px 0;
          line-height: 1.6;
        }

        .commercial-form {
          position: relative;
          text-align: left;
        }

        .commercial-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .commercial-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .commercial-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #334155;
        }

        .commercial-field input {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 14px;
          font-size: 1rem;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          background: #ffffff;
          color: #0f172a;
        }

        .commercial-field input:focus {
          outline: none;
          border-color: #1e88e5;
          box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.2);
        }

        /* The honeypot: off-screen, not display:none, which some bots skip. */
        .commercial-trap {
          position: absolute;
          left: -10000px;
          top: auto;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }

        .btn-commercial {
          display: block;
          margin: 0 auto;
          background: #1e88e5;
          color: white;
          border: none;
          padding: 14px 32px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          box-shadow: 0 4px 14px rgba(30, 136, 229, 0.3);
        }

        .btn-commercial:hover:not(:disabled) {
          background: #1565c0;
          transform: translateY(-2px);
        }

        .btn-commercial:disabled {
          background: #9ca3af;
          cursor: default;
          box-shadow: none;
        }

        .commercial-error {
          margin: 12px 0 0;
          text-align: center;
          color: #b91c1c;
          font-size: 0.95rem;
        }

        .commercial-done {
          margin: 0;
          padding: 16px 20px;
          background: #ffffff;
          border: 1px solid #bfdbfe;
          border-radius: 12px;
          color: #1e3a8a;
          font-weight: 600;
          font-size: 1.05rem;
        }

/* LawnTrack Coming Soon Section */
        .lawntrack-section {
          padding: 80px 24px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
        }

        .lawntrack-container {
          max-width: 600px;
          margin: 0 auto;
          text-align: center;
        }

        .lawntrack-badge {
          display: inline-block;
          background: #16a34a;
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .lawntrack-icon {
          width: 72px;
          height: 72px;
          margin: 0 auto 20px;
          background: #bbf7d0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #16a34a;
        }

        .lawntrack-icon svg {
          width: 36px;
          height: 36px;
        }

        .lawntrack-title {
          font-size: 2rem;
          font-weight: 700;
          color: #14532d;
          margin: 0 0 12px 0;
        }

        .lawntrack-subtitle {
          font-size: 1.15rem;
          color: #166534;
          margin: 0 0 16px 0;
          font-weight: 500;
        }

        .lawntrack-description {
          font-size: 1rem;
          color: #4d7c0f;
          margin: 0 0 28px 0;
          line-height: 1.6;
        }

        .btn-lawntrack {
          display: inline-block;
          background: #16a34a;
          color: white;
          padding: 14px 32px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: background 0.2s, transform 0.2s;
          box-shadow: 0 4px 14px rgba(22, 163, 74, 0.3);
        }

        .btn-lawntrack:hover {
          background: #15803d;
          transform: translateY(-2px);
        }

        .btn-lawntrack.disabled {
          background: #9ca3af;
          cursor: default;
          box-shadow: none;
        }

        .btn-lawntrack.disabled:hover {
          background: #9ca3af;
          transform: none;
        }

                /* CTA Section */
        .cta-section {
          padding: 80px 24px;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
        }

        .cta-container {
          max-width: 600px;
          margin: 0 auto;
          text-align: center;
        }

        .cta-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 16px 0;
        }

        .cta-subtitle {
          font-size: 1.1rem;
          color: #64748b;
          margin: 0 0 32px 0;
        }

        .cta-buttons {
          margin-bottom: 24px;
        }

        .btn-cta-primary {
          display: inline-block;
          background: #1e88e5;
          color: white;
          padding: 16px 40px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          transition: background 0.2s, transform 0.2s;
          box-shadow: 0 4px 14px rgba(30, 136, 229, 0.3);
        }

        .btn-cta-primary:hover {
          background: #1565c0;
          transform: translateY(-2px);
        }

        .cta-link {
          color: #1e88e5;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .cta-link:hover {
          color: #1565c0;
        }

        /* Footer */
        .landing-footer {
          background: #1e293b;
          padding: 48px 24px;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }

        .footer-logo {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
        }

        .footer-links {
          display: flex;
          gap: 32px;
        }

        .footer-links a {
          color: #94a3b8;
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-links a:hover {
          color: white;
        }

        .footer-copyright {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          /* Login has to stay reachable at every width - hiding .nav-link here
             took Pricing AND Login off the marketing nav on phones, leaving a
             customer on their phone with no way to sign in from the homepage.
             Nothing is hidden now: the bar wraps to a second centred row when
             the logo and the links cannot share one line, which is what
             happens at typical phone widths (<=400px). The hero reserves
             120px of top padding, so the taller bar still clears it. */
          .nav-container {
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px 16px;
            padding: 12px 16px;
          }

          .nav-logo {
            font-size: 1.15rem;
          }

          .logo-icon {
            font-size: 1.15rem;
          }

          .plans-strip-grid {
            grid-template-columns: 1fr;
          }

          .commercial-section {
            padding: 56px 16px;
          }

          .commercial-title {
            font-size: 1.4rem;
          }

          .commercial-fields {
            grid-template-columns: 1fr;
          }

          .btn-commercial {
            width: 100%;
          }

          .nav-links {
            flex-wrap: wrap;
            justify-content: center;
            gap: 14px;
          }

          .nav-link {
            font-size: 0.9rem;
          }

          .nav-btn-primary {
            padding: 8px 14px;
            font-size: 0.9rem;
          }

          .hero-title {
            font-size: 2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .hero-buttons {
            flex-direction: column;
            align-items: center;
          }

          .btn-hero-primary,
          .btn-hero-secondary {
            width: 100%;
            max-width: 280px;
            text-align: center;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .feature-card {
            padding: 24px;
          }

          .cta-title {
            font-size: 1.5rem;
          }

          .footer-links {
            gap: 24px;
          }
        }
      `}</style>
    </div>
  );
}

export default LandingPage;
